import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export const ONBOARDING_STEPS = [
  { path: "/onboarding/clinic", label: "Phòng khám" },
  { path: "/onboarding/services", label: "Dịch vụ" },
  { path: "/onboarding/doctors", label: "Bác sĩ" },
  { path: "/onboarding/schedule", label: "Lịch làm việc" },
  { path: "/onboarding/import", label: "Bệnh nhân" },
  { path: "/onboarding/done", label: "Hoàn tất" },
];

interface Props {
  /** 1-based step number among the 6 configuration steps. */
  step: number;
  title: string;
  description: string;
  /** Link + label to the real settings page that does the actual work. */
  ctaTo?: string;
  ctaLabel?: string;
  nextTo: string;
  backTo?: string;
  children?: ReactNode;
}

/** Guided setup shell: progress dots + guidance + a CTA to the real page + Back/Skip/Next nav. */
export function OnboardingShell({
  step,
  title,
  description,
  ctaTo,
  ctaLabel,
  nextTo,
  backTo,
  children,
}: Props) {
  const navigate = useNavigate();
  const total = ONBOARDING_STEPS.length;

  return (
    <div className="mx-auto w-full max-w-2xl font-sans">
      <div className="mb-2">
        <Badge variant="outline">Thiết lập ban đầu</Badge>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      {/* progress */}
      <div className="my-4 flex flex-wrap gap-1.5">
        {ONBOARDING_STEPS.map((s, i) => (
          <span
            key={s.path}
            title={s.label}
            className={cn(
              "h-1.5 min-w-6 flex-1 rounded-full",
              i < step ? "bg-primary" : "bg-border",
            )}
          />
        ))}
      </div>

      <Card>
        <CardContent className="grid gap-4 p-5">
          <div className="text-sm text-muted-foreground">
            Bước {step}/{total}
          </div>

          {children}

          {ctaTo && (
            <Button asChild variant="secondary" className="w-fit">
              <Link to={ctaTo}>
                <ExternalLink />
                {ctaLabel ?? "Mở trang cấu hình"}
              </Link>
            </Button>
          )}

          <div className="flex items-center justify-between gap-2">
            <div>
              {backTo && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(backTo)}
                >
                  <ArrowLeft />
                  Quay lại
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="link"
                onClick={() => navigate(nextTo)}
              >
                Bỏ qua
              </Button>
              <Button type="button" onClick={() => navigate(nextTo)}>
                Tiếp tục
                <ArrowRight />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
